<template>
  <div v-if="hasActions" class="element-actions" :style="styles">
    <template v-for="(item, index) in groupedActions" :key="`action-${index}`">
      <!-- Single action -->
      <MAction v-if="item.type === 'single'" :data="item.action" :registrable="true" />

      <!-- Grouped actions with dropdown -->
      <div v-else-if="item.type === 'group'" class="action-group">
        <MAction :data="item.mainAction" :in-group="true" :registrable="true" class="main-action" />
        <Button icon="pi pi-chevron-down" size="small" class="action-dropdown-toggle"
          @click="toggleDropdown($event, index)" aria-label="Показать дополнительные действия" />
        <Menu :ref="(el: any) => setMenuRef(el, index)" :model="getDropdownItems(item.actions)" :popup="true" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, inject } from 'vue'
import { usePageData } from '@/common/composables/usePageData'
import { useActionRoleAccess } from '@/common/composables/useActionRoleAccess'
import MAction from '@/components/platform/MAction.vue'
import Button from 'primevue/button'
import Menu from 'primevue/menu'
import { getPrimeIcon } from '@/common/utils/iconMapper'
import { useDropdownGroupActionRegistry } from '@/common/composables/useDropdownGroupActionRegistry'

interface Props {
  level?: 'form' | 'page' | 'overlay'
  pageId?: string | number
  position: string[]
}

const props = withDefaults(defineProps<Props>(), {
  level: 'form',
  pageId: undefined,
  position: () => [],
})

const { pageActions, pageRegistryFormId } = usePageData()
const { hasActionRoleAccess } = useActionRoleAccess()

const getDropDownName = (action: any): string => {
  const dropDown = action.dropDownActionName
  if (Array.isArray(dropDown)) {
    return dropDown[0]?._text?.trim() || ''
  }
  return dropDown?._text?.trim() || ''
}

const isDropDownMain = (action: any): boolean => {
  const dropDown = action.dropDownMain
  const value = Array.isArray(dropDown) ? dropDown[0]?._text : dropDown?._text
  return value === 'true' || value === true
}

const hasRequiredRole = (action: any): boolean => hasActionRoleAccess(action.roles)

const actions = computed(() => {
  const pageIdStr = props.pageId?.toString()
  const filtered = pageActions.value.filter((action: any) => {
    const levelMatch = action.layout?.level?._text === props.level
    const idMatch = (props.level === 'page' || props.level === 'overlay')
      ? action.layout?.id?._text === pageIdStr
      : true // For form level, no ID matching needed
    const positionMatch = props.position.includes(action.layout?.position?._text)
    const roleMatch = hasRequiredRole(action)

    // CRITICAL: Exclude ALL actions with dropDownMain="false" that have a dropDownActionName
    // These should NEVER render as standalone buttons - only in dropdowns
    const dropDownName = getDropDownName(action)

    // Handle dropDownMain which might be an array if it appears multiple times in XML
    const hasDropDownGroup = dropDownName !== ''

    if (hasDropDownGroup && !isDropDownMain(action)) {
      // This action should only appear in a dropdown menu, never standalone
      return false
    }

    return levelMatch && idMatch && positionMatch && roleMatch
  })

  return filtered
})

// Group actions by dropDownActionName, collecting from ALL positions globally
const groupedActions = computed(() => {
  const sorted = [...actions.value].sort((a, b) => {
    const sortA = parseInt(a._attributes?.sort) || 0
    const sortB = parseInt(b._attributes?.sort) || 0
    return sortA - sortB
  })

  // Build global groups map from ALL actions for dropdown groups
  const pageIdStr = props.pageId?.toString()
  const globalGroups: Map<string, any[]> = new Map()

  pageActions.value.forEach(action => {
    const dropDownName = getDropDownName(action)
    const roleMatch = hasRequiredRole(action)
    const levelMatch = action.layout?.level?._text === props.level
    const idMatch = props.level === 'page'
      ? action.layout?.id?._text === pageIdStr
      : true

    if (dropDownName !== '' && roleMatch && levelMatch && idMatch) {
      if (!globalGroups.has(dropDownName)) {
        globalGroups.set(dropDownName, [])
      }
      globalGroups.get(dropDownName)!.push(action)
    }
  })

  // Now process only the filtered actions for this position
  const groups: Map<string, any[]> = new Map()
  const ungrouped: any[] = []
  const processedGroups = new Set<string>()

  sorted.forEach(action => {
    const dropDownName = getDropDownName(action)
    const hasDropDownGroup = dropDownName !== ''

    if (hasDropDownGroup && !processedGroups.has(dropDownName)) {
      processedGroups.add(dropDownName)

      // Use the global group data (ALL actions with this dropDownActionName)
      const groupActions = globalGroups.get(dropDownName) || []

      // Current action should be the main action (since we filtered out non-main)
      // Add the group regardless - the current action is in the filtered list
      groups.set(dropDownName, groupActions)
    } else if (!hasDropDownGroup) {
      ungrouped.push(action)
    }
  })

  // Create final list
  const result: any[] = []

  // Add ungrouped actions
  ungrouped.forEach(action => {
    result.push({ type: 'single', action })
  })

  // Add grouped actions
  groups.forEach((groupActions, groupName) => {
    // Handle dropDownMain which might be an array if it appears multiple times in XML
    const mainAction = groupActions.find(a => isDropDownMain(a))

    result.push({
      type: 'group',
      groupName,
      mainAction: mainAction || groupActions[0],
      actions: groupActions
    })
  })

  return result
})

const hasActions = computed(() => groupedActions.value.length > 0)

// Menu refs management
const menuRefs = ref<Map<number, any>>(new Map())

const setMenuRef = (el: any, index: number) => {
  if (el) {
    menuRefs.value.set(index, el)
  }
}

const toggleDropdown = (event: Event, index: number) => {
  const menu = menuRefs.value.get(index)
  if (menu) {
    menu.toggle(event)
  }
}

// Get onAction from injection
const onAction = inject<((context: any) => void)>('onAction', () => { })

const executeDropdownAction = async (action: any) => {
  const ctx = {
    id: action._attributes ? +action._attributes.id : 0,
    name: action.name?._text || '',
    level: props.level,
    pageId: props.level === 'page' ? props.pageId : undefined,
    data: action
  }

  await onAction({ getContext: () => ctx })
}

const registryFormId = computed(() => {
  if (pageRegistryFormId.value) {
    const n = parseInt(pageRegistryFormId.value, 10)
    if (Number.isFinite(n)) return n
  }
  return 0
})

const { isDropdownMenuActionVisible, isDropdownMenuActionEnabled, dropdownActionLabel } = useDropdownGroupActionRegistry({
  groupedActions,
  formId: registryFormId,
  onActivateDropdown: executeDropdownAction,
})

// Format actions for PrimeVue Menu
const getDropdownItems = (actions: any[]) => {
  return actions
    .filter(a => hasRequiredRole(a) && isDropdownMenuActionVisible(a))
    .map(action => ({
      label: dropdownActionLabel(action),
      icon: getPrimeIcon(action.image?._text || ''),
      disabled: !isDropdownMenuActionEnabled(action),
      command: () => executeDropdownAction(action)
    }))
}

const styles = computed(() => {
  const isSpecial = props.position.includes('special')
  return {
    justifyContent: isSpecial ? 'flex-end' : '',
  }
})
</script>

<style lang="scss" scoped>
.element-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0 .05rem;
  min-height: 2.5rem;

  Fix text wrapping for all action buttons :deep(.m-button .m-label__text) {
    white-space: nowrap !important;
  }
}

.action-group {
  display: inline-flex;
  align-items: stretch;
  gap: 0;
  height: 2.5rem;

  .main-action {
    :deep(.p-button) {
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
      border-right: none !important;
      margin: 0 !important;
      height: 100%;
    }
  }
}

// Dropdown toggle button - very specific selectors to override PrimeVue
.action-dropdown-toggle {
  border-radius: 0 !important;
  border-top-right-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  border: 1px solid var(--primary-color) !important;
  border-left: 1px solid rgba(255, 255, 255, 0.3) !important;
  min-width: auto !important;
  width: 2rem !important;
  height: 100% !important;
  padding: 0 !important;
}


.action-dropdown-toggle,
.action-dropdown-toggle.p-button,
.action-dropdown-toggle.p-button.p-button-sm {
  background: var(--primary-color) !important;
  background-color: var(--primary-color) !important;
  color: white !important;

  .p-button-icon {
    font-size: 0.75rem !important;
    color: white !important;
  }

  .p-button-label {
    display: none;
  }
}

.action-dropdown-toggle:hover,
.action-dropdown-toggle.p-button:hover,
.action-dropdown-toggle.p-button.p-button-sm:hover,
.action-dropdown-toggle:enabled:hover,
.action-dropdown-toggle.p-button:enabled:hover,
.action-dropdown-toggle:not(:disabled):hover,
.action-dropdown-toggle.p-button:not(:disabled):hover {
  background: var(--p-blue-600) !important;
  background-color: var(--p-blue-600) !important;
  color: white !important;
  border-color: var(--p-blue-600) !important;

  .p-button-icon {
    color: white !important;
  }
}


.action-dropdown-toggle:focus,
.action-dropdown-toggle.p-button:focus {
  box-shadow: none !important;
  background: var(--primary-color) !important;
  background-color: var(--primary-color) !important;
}
</style>
